import { Icon, type IconName } from "@tutor/ui";
import { useState } from "react";
import { useLocale } from "../i18n";
import type * as api from "./api";
import { markAlertRead } from "./api";
import { errorKey } from "./errors";
import { pausedToday } from "./PauseBanner";
import { skillLabel } from "./skill-label";
import { useParentT, type ParentStringKey } from "./strings";

// The selected child's alerts, newest first, with mark-as-read. Reasons are enums from the
// engine; nothing the child said is shown. Today's high safety alert is outlined; the pause
// itself and its « Reprendre la séance » button sit above every tab (PauseBanner).

const ICONS: Readonly<Record<api.AlertReason, IconName>> = {
  safety_low: "alert",
  safety_high: "alert",
  frustration: "heart",
  plateau: "peak",
  daily_cap: "steady",
};

export function Alerts(props: {
  alerts: readonly api.ParentAlertRow[];
  onRead: (alertId: string) => void;
  onAuthError: (error: api.ApiError) => boolean;
}) {
  const { alerts, onRead, onAuthError } = props;
  const t = useParentT();
  const locale = useLocale();
  const [message, setMessage] = useState<ParentStringKey>();
  const [busy, setBusy] = useState<string>();
  const time = new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });

  const read = async (alertId: string) => {
    setBusy(alertId);
    const res = await markAlertRead(alertId);
    setBusy(undefined);
    if (res.ok || (res.error.kind === "http" && res.error.status === 404)) {
      onRead(alertId);
      return;
    }
    if (!onAuthError(res.error)) setMessage(errorKey(res.error));
  };

  return (
    <div className="pm-alerts">
      {message === undefined ? null : (
        <p className="parent-message" role="alert">
          {t(message)}
        </p>
      )}
      {alerts.length === 0 ? (
        <div className="pm-empty">
          <span className="pm-empty__icon">
            <Icon name="check" />
          </span>
          <p className="pm-empty__title">{t("alerts.empty")}</p>
          <p className="parent-muted">{t("alerts.emptyBody")}</p>
        </div>
      ) : (
        <ul className="pm-alert-list">
          {alerts.map((a) => {
            const unread = a.readAt === null;
            const paused = pausedToday(a);
            return (
              <li
                key={a.id}
                className="pm-alert"
                data-unread={unread ? "true" : undefined}
                data-reason={a.reason}
                data-paused={paused ? "true" : undefined}
              >
                <span className="pm-alert__icon">
                  <Icon name={ICONS[a.reason]} size={20} />
                </span>
                <div className="pm-alert__body">
                  <p className="pm-alert__title">{t(`alerts.reason.${a.reason}`)}</p>
                  <p className="pm-alert__meta">
                    <span className="pm-tag" data-urgency={a.urgency}>
                      {t(a.urgency === "now" ? "alerts.now" : "alerts.digest")}
                    </span>
                    <span>{time.format(Date.parse(a.createdAt))}</span>
                    {a.skillId === null ? null : (
                      <span>{t("alerts.skill", { skill: skillLabel(a.skillId, locale) })}</span>
                    )}
                  </p>
                </div>
                {unread ? (
                  <button
                    type="button"
                    className="pm-alert__read"
                    disabled={busy === a.id}
                    onClick={() => void read(a.id)}
                  >
                    {t("alerts.markRead")}
                  </button>
                ) : (
                  <span className="pm-alert__seen">{t("alerts.read")}</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
